import { SYNTHESISE_AI_TASKS, SubTaskId } from '../../../../src/commonConstants';
import { cache, getUserLikes, synthesiseAi } from '../apiRequests';
import { TUTORIAL_EVENT_TYPES, tutorialEmitter } from '../tutorials/tutorialEvents';
import {
	removeChildren,
	showAlert,
	showNonBlockingMessage,
	clearNonBlockingMessage,
	closeAllPanels,
} from './uiUtils';
import { tableToString } from '../misc';
import { MoodboardGraph } from '../graphs';
import {
	EngineId,
	LikedItem,
	NCNode,
	SynthesiseRecipe,
	SynthesiseRecipeInput,
} from '../../../../src/commonTypes';
import { showSynthesiseNodesPrompt } from './toolsUI';
import { WINDOW_BREAKPOINTS } from '../constants';
import { closeNotepad, openNotepad } from './notepad';
import { enableLightboxForImage } from './lightbox';
import { removeDuplicates } from '../../../../src/commonMisc';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import { SYNTHESISE_DOCK_TAB_ACTIVATED_EVENT, setActiveSynthesiseDockTab } from './synthesiseDock';
import { getSynthesiseIdeaInputDisplay } from './synthesiseIdeaInput';
import { getSynthesiseNodeInputContent } from './synthesiseNodeInput';
import {
	UPDATE_NODE_ON_BOARD_LABEL,
	synthesiseBoardActionLabel,
} from './synthesiseBoardTarget';
import {
	reconcileRecipeBoardInputs,
	resolveRecipeBoardNodes,
} from './synthesiseRecipeBoardNodes';
import {
	compileSynthesisePrompt,
	inferSynthesiseInputRole,
	promptForSynthesiseTask,
	synthesiseRoleLabel,
} from './synthesiseSlotCompiler';
import {
	classifyStage1Context,
	filterStage15Tasks,
	Stage1Context,
	Stage1ContextVariant,
} from './synthesiseCapabilityPolicy';

const synthesiseAiButton = document.querySelector<HTMLButtonElement>('#synthesise-ai')!;
const synthesiseAiContainer = document.querySelector<HTMLDivElement>('#synthesise-ai-container')!;
const form = synthesiseAiContainer.querySelector<HTMLFormElement>('#synthesise-ai-form')!;
const output = form.querySelector<HTMLDivElement>('.result')!;
const noResult = form.querySelector<HTMLParagraphElement>('.no-result')!;
const loadingIndicator = form.querySelector<HTMLImageElement>('.loading')!;
const likedItemCheckbox = form.querySelector<HTMLInputElement>(
	'.liked-input .checkbox-wrapper input'
)!;
const selectedItemCheckbox = form.querySelector<HTMLInputElement>(
	'.selected-input .checkbox-wrapper input'
)!;
const selectItemsButton = form.querySelector<HTMLInputElement>(
	'.selected-input .selected-item-wrapper button'
)!;
const aiInputImage = document.querySelector<HTMLDivElement>('.ai-input-image')!;
const imageContainer = aiInputImage.querySelector('.ai-input-image .image-container')!;
const aiInput = document.querySelector<HTMLDivElement>('.ai-input')!;
const originalInput = form.querySelector<HTMLTextAreaElement>('.original-input')!;
const originalInputSource = form.querySelector<HTMLDivElement>('.original-input-source')!;
const additionalInstructions = form.querySelector<HTMLTextAreaElement>('#additional-instructions')!;
const recipeInputList = form.querySelector<HTMLDivElement>('.recipe-input-list')!;
const recipeInputEmpty = form.querySelector<HTMLParagraphElement>('.recipe-input-empty')!;
const recipeStatus = form.querySelector<HTMLDivElement>('.recipe-status')!;
const addBoardInputButton = form.querySelector<HTMLButtonElement>('.add-board-input')!;
const addIdeaInputButton = form.querySelector<HTMLButtonElement>('.add-idea-input')!;
const addNoteInputButton = form.querySelector<HTMLButtonElement>('.add-note-input')!;
const boardInputSelection = form.querySelector<HTMLDivElement>('.board-input-selection')!;
const boardInputSelectionLabel = boardInputSelection.querySelector<HTMLSpanElement>('span')!;
const finishBoardInputSelectionButton = boardInputSelection.querySelector<HTMLButtonElement>(
	'.finish-board-input-selection'
)!;
const ideaInputPicker = form.querySelector<HTMLDivElement>('.idea-input-picker')!;
const ideaInputOptions = form.querySelector<HTMLDivElement>('.idea-input-options')!;
const ideaInputLoading = form.querySelector<HTMLParagraphElement>('.idea-input-loading')!;
const closeIdeaInputPicker = form.querySelector<HTMLButtonElement>('.close-idea-input-picker')!;
const toneSelect = form.querySelector<HTMLSelectElement>('[name="output-tone"]')!;
const lengthSelect = form.querySelector<HTMLSelectElement>('[name="output-length"]')!;
const formatSelect = form.querySelector<HTMLSelectElement>('[name="output-format"]')!;
const platformSelect = form.querySelector<HTMLSelectElement>('[name="output-platform"]')!;
const platformSetting = form.querySelector<HTMLElement>('.social-platform-setting')!;
const visualStyleSetting = form.querySelector<HTMLElement>('.visual-style-setting')!;
const visualStyleSelect = form.querySelector<HTMLSelectElement>('[name="visual-style"]')!;
const resultActions = form.querySelector<HTMLDivElement>('.result-actions')!;
const addToBoardButton = resultActions.querySelector<HTMLButtonElement>('.add-to-board')!;
const addToNotesButton = resultActions.querySelector<HTMLButtonElement>('.add-to-notes')!;
const regenerateButton = resultActions.querySelector<HTMLButtonElement>('.regenerate-result')!;
const copyResultButton = resultActions.querySelector<HTMLButtonElement>('.copy-result')!;
const taskSelector = synthesiseAiContainer.querySelector<HTMLFieldSetElement>('.first-option')!;
const secondTaskSelectorWrapper = document.querySelector(
	'.second-option-container'
) as HTMLDivElement;
const secondTaskSelector =
	synthesiseAiContainer.querySelector<HTMLFieldSetElement>('.second-option')!;
const likedInputContainer = synthesiseAiContainer.querySelector<HTMLDivElement>('.liked-input')!;
const selectedInputContainer =
	synthesiseAiContainer.querySelector<HTMLDivElement>('.selected-input')!;

type TaskId = keyof typeof SYNTHESISE_AI_TASKS;

let lastGeneratedRecipe: SynthesiseRecipe | undefined;
let recipeInputs: SynthesiseRecipeInput[] = [];
let originalRecipeInput: SynthesiseRecipeInput | undefined;
let activeRecipeNode: NCNode | undefined;
const materialisedOutputNodesByTask = new Map<string, NCNode>();

const TASK_CATEGORIES: Array<{ label: string; taskIds: TaskId[] }> = [
	{
		label: 'Create',
		taskIds: [
			'Prod-ideas',
			'Brief',
			'TechnicalSpec',
			'Post',
			'Tagline',
			'Synopsis',
			'Game',
			'Strategy',
			'Image',
			'Video',
		],
	},
	{ label: 'Analyse', taskIds: ['Audience', 'PESTEL', 'SWOT'] },
	{ label: 'Explore', taskIds: ['Brainstorming', 'SCAMPER'] },
	{ label: 'Transform content', taskIds: ['Summary', 'Extract', 'Unite'] },
];

const TASK_INPUT_HINTS: Partial<Record<TaskId, string>> = {
	'Prod-ideas': 'Best with: a problem or opportunity, stakeholder needs, concepts and constraints.',
	Brief: 'Best with: a product idea, audience, desired outcomes, requirements and constraints.',
	TechnicalSpec:
		'Best with: a product brief, user requirements, technical constraints, evidence and acceptance criteria.',
	Post: 'Best with: a message, audience, communication goal, brand voice and optional visual.',
	Tagline: 'Best with: positioning, audience, brand voice and the core promise.',
	Synopsis: 'Best with: a premise, characters, motivations, themes and desired story arc.',
	Game: 'Best with: a core concept, audience, platform, mechanics and visual references.',
	Strategy: 'Best with: positioning, stakeholders, motivations, brand voice and market evidence.',
	Image: 'Best with: a concept, visual references, brand colours and desired mood.',
	Video: 'Best with: a concept, source image, visual direction and desired movement.',
	Audience: 'Best with: the offering, stakeholder evidence and relevant motivations.',
};

const applyRecommendedSettings = (taskId: TaskId): void => {
	platformSetting.classList.toggle('hidden', taskId !== 'Post');
	if (activeRecipeNode) return;
	if (taskId === 'TechnicalSpec' || taskId === 'Brief') {
		toneSelect.value = 'Professional and precise';
		lengthSelect.value =
			taskId === 'TechnicalSpec' ? 'Develop it in detail' : 'Use an appropriate standard length';
		formatSelect.value = 'Use clear headings and bullet points';
	} else if (taskId === 'Post' || taskId === 'Tagline') {
		lengthSelect.value = 'Keep it concise';
		formatSelect.value = 'Use the most suitable format for this output';
	} else {
		toneSelect.selectedIndex = 0;
		lengthSelect.selectedIndex = 0;
		formatSelect.selectedIndex = 0;
	}
};

const existingOutputNodeForTask = (task: string): NCNode | undefined => {
	const remembered = materialisedOutputNodesByTask.get(task);
	if (!remembered) return undefined;
	return globalThis.neuroCreate.graph?.nodes.find((node) => node.uid === remembered.uid);
};

const rememberOutputNodeForTask = (task: string, node: NCNode): void => {
	materialisedOutputNodesByTask.set(task, node);
	activeRecipeNode = node;
};

const bindActiveRecipeNodeToTask = (task?: string | null): NCNode | undefined => {
	activeRecipeNode = task ? existingOutputNodeForTask(task) : undefined;
	return activeRecipeNode;
};

const createRecipeInputId = (): string =>
	globalThis.crypto?.randomUUID?.() || `recipe-input-${Date.now()}-${Math.random()}`;

const inferInputRole = inferSynthesiseInputRole;

const roleLabel = synthesiseRoleLabel;

const truncateInputLabel = (value: string): string =>
	value.length > 90 ? `${value.slice(0, 87)}…` : value;

const renderOriginalInputSource = (): void => {
	removeChildren(originalInputSource);
	if (!originalRecipeInput) {
		originalInputSource.classList.add('hidden');
		return;
	}

	originalInputSource.classList.remove('hidden');
	if (originalRecipeInput.src) {
		const image = document.createElement('img');
		image.src = originalRecipeInput.src;
		image.alt = '';
		originalInputSource.appendChild(image);
	}
	const copy = document.createElement('span');
	const source = document.createElement('small');
	const sourceLocation =
		originalRecipeInput.source === 'idea-box'
			? 'Idea Box'
			: originalRecipeInput.source === 'notepad'
				? 'Notepad'
				: originalRecipeInput.source === 'manual'
					? 'manual input'
					: 'Board';
	source.textContent =
		originalRecipeInput.role === 'visual-reference'
			? `Image reference · ${sourceLocation}`
			: `From ${sourceLocation}`;
	const label = document.createElement('strong');
	label.textContent = originalRecipeInput.label;
	copy.append(source, label);
	const role = document.createElement('span');
	role.className = 'recipe-input-role';
	role.textContent = roleLabel(originalRecipeInput.role);
	originalInputSource.append(copy, role);
};

const setOriginalRecipeInput = (input: SynthesiseRecipeInput): void => {
	originalRecipeInput = { ...input };
	originalInput.value = input.value || '';
	renderOriginalInputSource();
	markRecipeChanged();
};

const ensureManualOriginalInput = (): void => {
	if (originalRecipeInput || !originalInput.value.trim()) return;
	originalRecipeInput = {
		id: createRecipeInputId(),
		role: 'primary-concept',
		label: truncateInputLabel(originalInput.value.trim()),
		value: originalInput.value.trim(),
		source: 'manual',
	};
};

const markRecipeChanged = (): void => {
	if (!activeRecipeNode && output.classList.contains('hidden')) return;
	recipeStatus.textContent = 'Context changed · regenerate before updating the output';
	recipeStatus.classList.remove('hidden', 'up-to-date');
	recipeStatus.classList.add('stale');
	addToBoardButton.disabled = true;
};

const syncIdeaInputPickerSelections = (): void => {
	ideaInputOptions.querySelectorAll<HTMLButtonElement>('.idea-input-option').forEach((button) => {
		const selected = recipeInputs.some(
			(input) => input.source === 'idea-box' && input.id === button.dataset.inputId
		);
		button.classList.toggle('added', selected);
		button.setAttribute('aria-pressed', String(selected));
	});
};

const renderRecipeInputs = (): void => {
	removeChildren(recipeInputList);
	recipeInputEmpty.classList.toggle('hidden', recipeInputs.length > 0);

	for (const input of recipeInputs) {
		const item = document.createElement('article');
		item.className = 'recipe-input-card';
		item.dataset.inputId = input.id;

		const content = document.createElement('div');
		content.className = 'recipe-input-content';
		if (input.src) {
			const image = document.createElement('img');
			image.src = input.src;
			image.alt = '';
			content.appendChild(image);
		}
		const text = document.createElement('div');
		const source = document.createElement('small');
		source.textContent =
			input.source === 'idea-box' ? 'Idea Box' : input.source === 'notepad' ? 'Notepad' : 'Board';
		const label = document.createElement('strong');
		label.textContent = input.label;
		text.append(source, label);
		content.appendChild(text);

		const role = document.createElement('span');
		role.className = 'recipe-input-role';
		role.textContent = roleLabel(input.role);

		const remove = document.createElement('button');
		remove.type = 'button';
		remove.className = 'remove-recipe-input';
		remove.setAttribute('aria-label', `Remove ${input.label}`);
		remove.textContent = '×';
		remove.addEventListener('click', () => {
			recipeInputs = recipeInputs.filter((candidate) => candidate.id !== input.id);
			renderRecipeInputs();
			syncIdeaInputPickerSelections();
			markRecipeChanged();
			const graph = globalThis.neuroCreate.graph;
			const boardSelection = graph?.selections.synthesiseNodes;
			if (input.nodeUid && boardSelection) {
				const selectedIndex = boardSelection.findIndex((node) => node.uid === input.nodeUid);
				if (selectedIndex !== -1) boardSelection.splice(selectedIndex, 1);
				graph.onTick();
				showSynthesiseNodesPrompt(boardSelection);
			} else {
				updateTaskOptionsInPanel();
			}
		});

		item.append(content, role, remove);
		recipeInputList.appendChild(item);
	}
};

const addRecipeInput = (input: SynthesiseRecipeInput): boolean => {
	const duplicate = [originalRecipeInput, ...recipeInputs]
		.filter(Boolean)
		.some(
			(candidate) =>
				(input.nodeUid && candidate?.nodeUid === input.nodeUid) ||
				(!input.nodeUid && candidate?.source === input.source && candidate.value === input.value)
		);
	if (duplicate) return false;
	recipeInputs.push(input);
	renderRecipeInputs();
	markRecipeChanged();
	if (recipeInputs.length === 1) tutorialEvents.synthesiseContextUpdated();
	return true;
};

const recipeInputFromNode = (node: NCNode, isPrimary = false): SynthesiseRecipeInput => {
	const content = getSynthesiseNodeInputContent(node);
	return {
		id: createRecipeInputId(),
		role: inferInputRole({
			type: content.type,
			label: content.label,
			src: content.src,
			isPrimary,
		}),
		label: content.label,
		value: content.value,
		src: content.src,
		nodeUid: node.uid,
		source: 'board',
	};
};

/**
 * Makes the currently selected board node the recipe's primary input.
 * Selecting a different node is an explicit change of focus, so it replaces
 * any previous original input while leaving additional context untouched.
 */
const syncActiveBoardNodeToOriginalInput = (): boolean => {
	const graph = globalThis.neuroCreate.graph;
	if (!graph || graph.graphMode !== 'moodboard') return false;
	const node = graph.activeNode;
	if (!node) return false;
	const input = recipeInputFromNode(node, true);
	const alreadyCurrent =
		originalRecipeInput?.nodeUid === node.uid &&
		originalRecipeInput.value === input.value &&
		originalRecipeInput.src === input.src;
	if (alreadyCurrent) return false;
	recipeInputs = recipeInputs.filter((candidate) => candidate.nodeUid !== node.uid);
	renderRecipeInputs();
	setOriginalRecipeInput(input);
	updateTaskOptionsInPanel();
	return true;
};

const syncSelectedNodesIntoRecipe = (): void => {
	const selectedNodes = globalThis.neuroCreate.graph?.selections.synthesiseNodes;
	if (!selectedNodes) return;
	const nextInputs = reconcileRecipeBoardInputs(recipeInputs, selectedNodes, recipeInputFromNode);
	const previousIds = recipeInputs.map((input) => input.id).join('\u0000');
	const nextIds = nextInputs.map((input) => input.id).join('\u0000');
	if (previousIds === nextIds) return;
	recipeInputs = nextInputs;
	renderRecipeInputs();
	markRecipeChanged();
};

const showIdeaInputPicker = async (): Promise<void> => {
	ideaInputPicker.classList.remove('hidden');
	ideaInputLoading.classList.remove('hidden');
	removeChildren(ideaInputOptions);
	const { boardId } = document.body.dataset;
	if (!boardId) return;
	try {
		const likedItems = await getUserLikes(
			boardId,
			undefined,
			window.location.hash.includes('team')
		);
		ideaInputLoading.classList.add('hidden');
		if (likedItems.length === 0) {
			ideaInputOptions.textContent = 'Your Idea Box is empty.';
			return;
		}
		for (const likedItem of likedItems) {
			const display = getSynthesiseIdeaInputDisplay(likedItem);
			const pickerInput: SynthesiseRecipeInput = {
				id: likedItem.clientId || likedItem._id || createRecipeInputId(),
				role: inferInputRole({
					type: likedItem.type,
					label: display.title,
					src: display.imageUrl,
				}),
				label: display.title,
				value: display.context,
				src: display.imageUrl,
				source: 'idea-box',
			};
			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'idea-input-option';
			button.dataset.inputId = pickerInput.id;
			button.setAttribute('aria-pressed', 'false');
			button.title = display.detail || display.title;
			if (display.imageUrl) {
				button.classList.add('with-image');
				const image = document.createElement('img');
				image.src = display.imageUrl;
				image.alt = '';
				button.appendChild(image);
			}
			const copy = document.createElement('span');
			copy.className = 'idea-input-copy';
			const title = document.createElement('strong');
			title.textContent = display.title;
			copy.appendChild(title);
			if (display.detail) {
				const detail = document.createElement('small');
				detail.textContent = display.detail;
				copy.appendChild(detail);
			}
			button.appendChild(copy);
			button.addEventListener('click', () => {
				const isSelected = recipeInputs.some(
					(input) => input.source === 'idea-box' && input.id === pickerInput.id
				);
				if (isSelected) {
					recipeInputs = recipeInputs.filter(
						(input) => !(input.source === 'idea-box' && input.id === pickerInput.id)
					);
					renderRecipeInputs();
					markRecipeChanged();
				} else {
					addRecipeInput(pickerInput);
				}
				syncIdeaInputPickerSelections();
				updateTaskOptionsInPanel();
			});
			ideaInputOptions.appendChild(button);
		}
		syncIdeaInputPickerSelections();
	} catch {
		ideaInputLoading.classList.add('hidden');
		ideaInputOptions.textContent = 'Unable to load the Idea Box right now.';
	}
};

const getCurrentRecipeInputs = (): SynthesiseRecipeInput[] => {
	const nodes = globalThis.neuroCreate.graph?.nodes || [];
	ensureManualOriginalInput();
	const primary = originalRecipeInput
		? [
				{
					...originalRecipeInput,
					label:
						originalRecipeInput.label ||
						truncateInputLabel(originalInput.value.trim() || 'Original input'),
					value: originalInput.value.trim() || undefined,
				},
			]
		: [];
	const additional = recipeInputs.map((input) => {
		if (!input.nodeUid) return { ...input };
		const currentNode = nodes.find((node) => node.uid === input.nodeUid);
		if (!currentNode) return { ...input };
		const content = getSynthesiseNodeInputContent(currentNode);
		return {
			...input,
			label: content.label,
			value: content.value,
			src: content.src,
			role: inferInputRole({
				type: content.type,
				label: content.label,
				src: content.src,
			}),
		};
	});
	return [...primary, ...additional];
};

const buildRecipePrompt = (recipe: SynthesiseRecipe): string => {
	const compiled = compileSynthesisePrompt(recipe);
	return promptForSynthesiseTask(compiled, recipe.task);
};

/** Short human label used to group Conceptual Territories — never the compiled LLM prompt. */
const conceptualTerritoryTitleFromRecipe = (recipe: SynthesiseRecipe): string => {
	const primary =
		recipe.inputs.find((input) => input.role === 'primary-concept' || input.role === 'brief') ||
		recipe.inputs[0];
	const label = (primary?.label || primary?.value || '').replace(/\s+/g, ' ').trim();
	if (label && !/^Task:\s*/i.test(label)) return truncateInputLabel(label);
	return truncateInputLabel(recipe.task);
};

const createCurrentRecipe = (task: TaskId | SubTaskId): SynthesiseRecipe => {
	const inputs = getCurrentRecipeInputs();
	const draft: SynthesiseRecipe = {
		task,
		inputs,
		instructions: additionalInstructions.value.trim() || undefined,
		settings: {
			tone: toneSelect.value,
			length: lengthSelect.value,
			format: formatSelect.value,
			...(task === 'Post'
				? { platform: platformSelect.value as 'LinkedIn' | 'Reddit' | 'X' | 'Instagram' }
				: {}),
		},
		inputSnapshot: '',
		generatedAt: Date.now(),
	};
	draft.inputSnapshot = buildRecipePrompt(draft);
	return draft;
};

const toggleNodeItems = (nodeItemContainer: HTMLDivElement) => {
	const { container } = nodeItemContainer.dataset;
	const checkbox = nodeItemContainer.querySelector<HTMLInputElement>('input')!;
	if (container) {
		const nodeItems = nodeItemContainer.querySelector<HTMLDivElement>(`.${container}`)!;
		if (checkbox.checked) {
			nodeItems.classList.remove('hidden');
		} else {
			nodeItems.classList.add('hidden');
		}
	}
};

export const addNumberOfNodeItems = (nodeItemContainer: HTMLDivElement): void => {
	// The current three-step panel keeps these legacy inputs hidden and no
	// longer renders their old `.count` badges. Opening the panel from a node
	// must not abort before that node is copied into the original input.
	const count = nodeItemContainer.querySelector<HTMLSpanElement>('.count');
	if (!count) return;
	const nodeItems: number = nodeItemContainer.querySelectorAll('li').length;
	count.textContent = '';
	if (nodeItems > 0) {
		count.textContent = `${nodeItems.toString()} `;
	} else {
		count.textContent = '';
	}
};
globalThis.addNumberOfNodeItems = addNumberOfNodeItems;

const updateSelectedNodesInPanel = (): void => {
	selectItemsButton.disabled = !selectedItemCheckbox.checked;
};

const handleSelectItems = (event: Event): void => {
	event.preventDefault();
	const graph = globalThis.neuroCreate.graph;
	if (!graph) return;
	if (graph.selections.synthesiseNodes) {
		syncSelectedNodesIntoRecipe();
		graph.stopSelectingNodes('common-ideas');
		boardInputSelection.classList.add('hidden');
		addBoardInputButton.textContent = '+ Board items';
		updateTaskOptionsInPanel();
		return;
	}

	tutorialEvents.selectingItems();
	graph.startSelectingNodes(
		'synthesiseNodes',
		resolveRecipeBoardNodes(recipeInputs, (uid) => graph.getNode(uid)),
		'common-ideas'
	);
	showSynthesiseNodesPrompt(graph.selections.synthesiseNodes!);
	updateBoardInputSelectionUi();
};

const updateBoardInputSelectionUi = (): void => {
	const selection = globalThis.neuroCreate.graph?.selections.synthesiseNodes;
	const isSelecting = selection !== undefined;
	boardInputSelection.classList.toggle('hidden', !isSelecting);
	addBoardInputButton.textContent = isSelecting ? 'Done selecting' : '+ Board items';
	if (isSelecting) {
		boardInputSelectionLabel.textContent =
			selection.length === 0
				? 'Select the board items to use as context.'
				: `${selection.length} board item${selection.length === 1 ? '' : 's'} selected.`;
	}
};

const updateLikedNodesInPanel = (): void => {
	if (globalThis.neuroCreate.graph) {
		const likedItems = removeDuplicates(globalThis.neuroCreate.graph.getLikedNodeLabels());
		const likedInput = document.querySelector<HTMLDivElement>('.liked-input')!;
		const likedItemWrapper = document.querySelector<HTMLDivElement>('.liked-items')!;
		const noLikedItem = document.querySelector<HTMLParagraphElement>(
			'.liked-item-wrapper .no-item'
		)!;

		removeChildren(likedItemWrapper);

		// add liked items to the list
		if (likedItems.length > 0) {
			noLikedItem.classList.add('hidden');
			for (const likedItem of likedItems) {
				const likedItemElement = document.createElement('li');
				likedItemElement.classList.add('liked-item');
				likedItemElement.innerText = likedItem;
				likedItemWrapper.appendChild(likedItemElement);
			}
		} else {
			noLikedItem.classList.remove('hidden');
		}
		addNumberOfNodeItems(likedInput);
	}
};

const getStage1Context = (): Stage1Context => ({
	primaryText: originalInput.value,
	primaryVisualSrc: originalRecipeInput?.src,
	primaryRole: originalRecipeInput?.role,
	additionalInputs: recipeInputs,
});

const getStage1ContextVariant = (): Stage1ContextVariant =>
	classifyStage1Context(getStage1Context());

const filterAvailableTasks = (variant: Stage1ContextVariant): TaskId[] => {
	const allTaskIds = Object.keys(SYNTHESISE_AI_TASKS) as TaskId[];
	// show variant for debugging
	const variantWrapper = document.querySelector<HTMLSpanElement>(
		'#synthesise-ai-container .variant'
	);
	if (variantWrapper) {
		variantWrapper.innerText = `Debug info: ${variant}`;
	}
	return filterStage15Tasks(allTaskIds, getStage1Context());
};

type SynthesiseVariant =
	| 'Recipe'
	| 'Variant 1' // Selected items, 1-4 words
	| 'Variant 2' // Selected items, 10+ words
	| 'Variant 3' // Selected items, 5-9 words
	| 'Variant 4' // Free text, 4+ words
	| 'Variant 4-A' // Free text, 1-3 words
	| 'Variant 5-A' // Free text + selected items, 1-3 words
	| 'Variant 5-B' // Free text + selected items, 4+ words
	| 'Variant 6-Image'
	| 'Invalid Input';

const getVariant = (): SynthesiseVariant => {
	return recipeInputs.length > 0 ||
		originalInput.value.trim().length > 0 ||
		Boolean(originalRecipeInput?.src)
		? 'Recipe'
		: 'Invalid Input';
};

const clickLabels = (label: string) => {
	if (label) {
		const labelElement = [...taskSelector.querySelectorAll<HTMLLabelElement>('label')].find(
			(candidate) => candidate.htmlFor === label
		);
		labelElement?.click();
		if (label === 'SCAMPER' || label === 'SWOT' || label === 'PESTEL') {
			const secondLabelElement = secondTaskSelector.querySelector('label') as HTMLLabelElement;
			secondLabelElement?.click();
		} else if (!labelElement) {
			const secondLabelElement = [
				...secondTaskSelector.querySelectorAll<HTMLLabelElement>('label'),
			].find((candidate) => candidate.htmlFor === label);
			secondLabelElement?.click();
		}
	}
	return;
};

const preSelectTasks = (
	engine?: string,
	firstRecommender?: string,
	secondRecommender?: string
): void => {
	if (firstRecommender && firstRecommender === 'CREATIVE') {
		clickLabels('SCAMPER');
	} else if (firstRecommender && firstRecommender === 'COPYWRITER') {
		clickLabels('Tagline');
	} else if (firstRecommender && firstRecommender === 'PRODUCT_DESIGN') {
		clickLabels('Prod-ideas');
	} else if (secondRecommender && secondRecommender === 'BRANDING') {
		clickLabels('Strategy');
	} else if (secondRecommender && secondRecommender === 'PRODUCT') {
		clickLabels('Brief');
	} else if (secondRecommender && secondRecommender === 'STORY') {
		clickLabels('Synopsis');
	} else if (engine && engine === 'Games') {
		clickLabels('Game');
	} else {
		// if no option was selected, select the first one
		taskSelector.querySelector<HTMLLabelElement>('label')?.click();
	}
};

function getCurrentlySelectedTaskOptions() {
	// get selected option from task selector
	const selectedOption = taskSelector.querySelector<HTMLInputElement>('input:checked')?.value;
	// get selected sub option from second task selector
	const selectedSubOption =
		secondTaskSelector.querySelector<HTMLInputElement>('input:checked')?.value;
	return { selectedOption, selectedSubOption };
}

function getRecommenderAnswers() {
	const firstRecommenderAnswer = document.querySelector('body')?.dataset
		.firstRecommenderAnswer as string;
	const secondRecommenderAnswer = document.querySelector('body')?.dataset
		.secondRecommenderAnswer as string;
	return { firstRecommenderAnswer, secondRecommenderAnswer };
}

function reconstructTaskOptions(filteredTaskIds: TaskId[]) {
	removeChildren(taskSelector);
	for (const category of TASK_CATEGORIES) {
		const taskIds = category.taskIds.filter((taskId) => filteredTaskIds.includes(taskId));
		if (taskIds.length === 0) continue;
		const categoryElement = document.createElement('div');
		categoryElement.className = 'output-category';
		const heading = document.createElement('h4');
		heading.textContent = category.label;
		const choices = document.createElement('div');
		choices.className = 'output-category-options';
		for (const key of taskIds) {
			const option = document.createElement('input');
			const label = document.createElement('label');
			option.type = 'radio';
			option.name = 'first-option';
			option.id = key;
			option.value = key;
			label.htmlFor = key;
			label.innerText = key === 'Post' ? 'Social Post' : SYNTHESISE_AI_TASKS[key].label || key;
			label.title = SYNTHESISE_AI_TASKS[key].name;
			choices.append(option, label);
		}
		categoryElement.append(heading, choices);
		taskSelector.appendChild(categoryElement);
	}

	const description = form.querySelector('.description') as HTMLParagraphElement;
	const firstOptions = taskSelector.querySelectorAll<HTMLLabelElement>('label');
	firstOptions.forEach((firstOption) => {
		firstOption.addEventListener('click', (event) => {
			if (event.isTrusted) {
				bindActiveRecipeNodeToTask(firstOption.htmlFor);
				markRecipeChanged();
				applyRecommendedSettings(firstOption.htmlFor as TaskId);
			}
			showSubOption(event);
			showDescription(event, description);

			setTimeout(() => {
				const imageSubtask =
					secondTaskSelector.querySelector<HTMLInputElement>('input:checked')?.value;
				handleGenerateImageAndVideoButton(firstOption.innerText, imageSubtask);
			}, 100);
		});
	});
}

function setSelectedTaskOptions(
	selectedOption: string | undefined,
	selectedSubOption: string | undefined,
	filteredTaskIds: TaskId[]
) {
	if (selectedOption === 'Image' || selectedOption === 'Video') {
		// trigger handleGenerateImageButton when the variant changed but the selected option is still image
		const imageSubtask = secondTaskSelector.querySelector<HTMLInputElement>('input:checked')?.value;
		handleGenerateImageAndVideoButton(selectedOption, imageSubtask);
	}

	// set selected option because list was repopulated
	if (selectedOption && filteredTaskIds.includes(selectedOption as TaskId)) {
		clickLabels(selectedOption);

		// set selected sub option because list was repopulated
		if (selectedSubOption) {
			addSubTaskOptions(selectedOption as TaskId);
			clickLabels(selectedSubOption);
		}
	} else {
		const { firstRecommenderAnswer, secondRecommenderAnswer } = getRecommenderAnswers();
		const engine = document.querySelector('body')?.dataset.engine as EngineId;

		// pre-select option if there was no a previous selection
		preSelectTasks(engine, firstRecommenderAnswer, secondRecommenderAnswer);
	}
}

export const updateTaskOptionsInPanel = (): void => {
	syncSelectedNodesIntoRecipe();
	updateBoardInputSelectionUi();
	const { selectedOption, selectedSubOption } = getCurrentlySelectedTaskOptions();

	const variant = getVariant();
	const filteredTaskIds = filterAvailableTasks(getStage1ContextVariant());

	reconstructTaskOptions(filteredTaskIds);

	form.querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled =
		variant === 'Invalid Input';

	setSelectedTaskOptions(selectedOption, selectedSubOption, filteredTaskIds);
};

const handleGenerateImageAndVideoButton = (firstOption: string, subtask?: string) => {
	// remove the previous generateImageButton if it exists
	const buttonContainer = form.querySelector('.button-container');
	buttonContainer?.querySelector('image-generator-with-quota')?.remove();
	buttonContainer?.querySelector('video-generator-with-quota')?.remove();
	const imageTimeHint = form.querySelector<HTMLElement>('.image-time-hint');
	const videoTimeHint = form.querySelector<HTMLElement>('.video-time-hint');
	const setMediaTimeHint = (kind: 'image' | 'video' | null) => {
		imageTimeHint?.classList.toggle('hidden', kind !== 'image');
		videoTimeHint?.classList.toggle('hidden', kind !== 'video');
		if (imageTimeHint) imageTimeHint.hidden = kind !== 'image';
		if (videoTimeHint) videoTimeHint.hidden = kind !== 'video';
	};

	const submit = buttonContainer?.querySelector('button.submit') as HTMLButtonElement;

	if (firstOption === 'Image') {
		// hide the submit button to replace it with the image generator
		submit.classList.add('hidden');
		setMediaTimeHint('image');

		const generateImageButton = document.createElement('image-generator-with-quota');

		generateImageButton.classList.add('submit');
		generateImageButton.align = 'center';
		generateImageButton.isDisabled = true;

		const variant = getVariant();

		if (subtask !== undefined) {
			generateImageButton.isDisabled = variant === 'Invalid Input';
		} else {
			const subtasks = document.querySelectorAll<HTMLInputElement>(
				'#synthesise-ai-form .second-option input'
			);
			subtasks.forEach((subtask) => {
				subtask.addEventListener('click', () => {
					generateImageButton.isDisabled = !subtask.checked || variant === 'Invalid Input';
				});
			});
		}

		generateImageButton.onClick = () => {
			handleSubmit();
		};
		buttonContainer?.append(generateImageButton);
	} else if (firstOption === 'Video') {
		submit.classList.add('hidden');
		setMediaTimeHint('video');

		const generateVideoButton = document.createElement('video-generator-with-quota');

		generateVideoButton.classList.add('submit');
		generateVideoButton.align = 'center';
		generateVideoButton.isDisabled = true;

		const variant = getVariant();

		if (subtask !== undefined) {
			generateVideoButton.isDisabled = variant === 'Invalid Input';
		} else {
			const subtasks = document.querySelectorAll<HTMLInputElement>(
				'#synthesise-ai-form .second-option input'
			);
			subtasks.forEach((subtask) => {
				subtask.addEventListener('click', () => {
					generateVideoButton.isDisabled = !subtask.checked || variant === 'Invalid Input';
				});
			});
		}

		generateVideoButton.onClick = () => {
			handleSubmit();
		};
		buttonContainer?.append(generateVideoButton);
	} else {
		setMediaTimeHint(null);
		buttonContainer?.querySelector('image-generator-with-quota')?.remove();
		buttonContainer?.querySelector('video-generator-with-quota')?.remove();
		if (submit.classList.contains('hidden')) {
			submit.classList.remove('hidden');
		}
	}
};

const showSubOption = (event: Event): void => {
	const firstOption = event.target as HTMLLabelElement;
	addSubTaskOptions(firstOption.htmlFor as TaskId);
};

const addSubTaskOptions = (taskId: TaskId): void => {
	const prompt = secondTaskSelectorWrapper.querySelector('.prompt') as HTMLSpanElement;
	prompt.innerText = '';
	const task = SYNTHESISE_AI_TASKS[taskId];
	const description = form.querySelector('.second-description') as HTMLParagraphElement;

	description.closest('div')!.style.display =
		taskId === 'Image' || taskId === 'Video' ? 'block' : 'flex';

	removeChildren(secondTaskSelector);

	if (task.options) {
		if (task['prompt']) {
			prompt.innerText = '';
			prompt.innerText = task['prompt'];
		}

		secondTaskSelectorWrapper.classList.add('show');
		for (const [key, subTask] of Object.entries(task.options)) {
			const option = document.createElement('input');
			const label = document.createElement('label');
			option.type = 'radio';
			option.value = key;
			option.id = key;
			option.name = 'second-option';
			label.htmlFor = key;
			label.innerText = subTask['optionName'];
			label.title = subTask['name'];
			secondTaskSelector.appendChild(option);
			secondTaskSelector.appendChild(label);
			if (subTask.requiresAdvanced && document.body.dataset.hasAdvancedAi !== 'true') {
				label.classList.add('soft-disabled');
				label.title += ' Upgrade your plan to enable this feature!';
			}
		}

		const secondOptions = secondTaskSelector.querySelectorAll('label');

		removeChildren(description);
		secondOptions.forEach((secondOption) => {
			secondOption.addEventListener('click', (event) => {
				showDescription(event, description);
			});
		});
		const isMediaTask = taskId === 'Image' || taskId === 'Video';
		visualStyleSetting.classList.toggle('hidden', !isMediaTask);
		form
			.querySelectorAll<HTMLElement>('.language-output-setting')
			.forEach((setting) => setting.classList.toggle('hidden', isMediaTask));
		if (isMediaTask) {
			removeChildren(visualStyleSelect);
			for (const [key, subTask] of Object.entries(task.options)) {
				const option = document.createElement('option');
				option.value = key;
				option.textContent = subTask.optionName;
				option.disabled = Boolean(
					subTask.requiresAdvanced && document.body.dataset.hasAdvancedAi !== 'true'
				);
				visualStyleSelect.appendChild(option);
			}
			const selected = secondTaskSelector.querySelector<HTMLInputElement>('input:checked')?.value;
			visualStyleSelect.value = selected || visualStyleSelect.options[0]?.value || '';
			secondTaskSelectorWrapper.classList.remove('show');
			if (visualStyleSelect.value) clickLabels(visualStyleSelect.value);
		}
	} else {
		secondTaskSelectorWrapper.classList.remove('show');
		visualStyleSetting.classList.add('hidden');
		form
			.querySelectorAll<HTMLElement>('.language-output-setting')
			.forEach((setting) => setting.classList.remove('hidden'));
		removeChildren(description);
	}
};

const setLoading = (): void => {
	output.innerText = '';
	noResult.classList.add('hidden');
	loadingIndicator.classList.remove('hidden');
	resultActions.classList.add('hidden');
	synthesiseAiContainer.scrollTop = synthesiseAiContainer.scrollHeight;
};

const removeLoading = (): void => {
	loadingIndicator.classList.add('hidden');
};

const displayOutput = (
	inputPhrase: string,
	taskId: TaskId | SubTaskId,
	firstOutput: string,
	secondOutput?: string,
	isExistingOutput = false
): void => {
	if (taskId.includes('Image')) {
		if (firstOutput) {
			output.innerHTML = '';
			const img = document.createElement('img');
			img.src = secondOutput || firstOutput;
			img.alt = 'Synthesise AI generated image';
			img.width = 240;
			enableLightboxForImage(img);
			output.append(img);
		} else {
			output.innerHTML = '';
		}
	} else if (taskId.includes('Video')) {
		if (firstOutput) {
			output.innerHTML = '';
			const video = document.createElement('video');
			video.src = secondOutput || firstOutput;
			video.controls = true;
			video.width = 240;
			output.append(video);
		} else {
			output.innerHTML = '';
		}
	} else {
		let result = '';
		if (secondOutput) {
			result = `${firstOutput}

${secondOutput}`;
		} else {
			result = firstOutput;
		}
		output.innerHTML = result;
	}

	output.dataset.task = taskId;
	const outputToSave = secondOutput || firstOutput;
	output.dataset.outputToSave = outputToSave;

	loadingIndicator.classList.add('hidden');
	output.innerText === '' && output.innerHTML === ''
		? output.classList.add('hidden')
		: output.classList.remove('hidden');
	resultActions.classList.toggle('hidden', output.classList.contains('hidden'));
	const existingOutputNode = bindActiveRecipeNodeToTask(taskId);
	addToBoardButton.disabled = false;
	addToBoardButton.textContent = synthesiseBoardActionLabel(Boolean(existingOutputNode));
	addToNotesButton.disabled = false;
	addToNotesButton.textContent = 'Add to notes';
	regenerateButton.disabled = false;
	regenerateButton.textContent = existingOutputNode
		? 'Regenerate from current inputs'
		: 'Regenerate';
	copyResultButton.disabled = false;
	copyResultButton.textContent = 'Copy';
	if (existingOutputNode) {
		recipeStatus.textContent = 'New version ready · update the board output when you are happy';
		recipeStatus.classList.remove('hidden', 'stale', 'up-to-date');
	}

	// Cache the result if it's a valid task with a 'likeType'
	let likeType = SYNTHESISE_AI_TASKS[taskId as TaskId]?.likeType as LikedItem['type'];
	if (!likeType) {
		const [parentTaskId] = taskId.split('-');
		const parentTask = SYNTHESISE_AI_TASKS[parentTaskId as TaskId];
		if (parentTask?.options) {
			likeType = parentTask.options[taskId as SubTaskId]?.likeType as LikedItem['type'];
		}
	}
	if (likeType && !isExistingOutput) {
		// No need to await
		cache(document.body.dataset.boardId!, likeType, outputToSave, null, inputPhrase);
	}

	if (!isExistingOutput) tutorialEvents.synthesiseResult();
};

const getResultValue = (): string => {
	const copiedOutput = output.cloneNode(true) as HTMLElement;
	const tableContent = copiedOutput.querySelector('table');
	if (tableContent) {
		const div = document.createElement('div');
		div.innerHTML = tableToString(tableContent);
		copiedOutput.insertBefore(div, tableContent);
		tableContent.remove();
		return copiedOutput.innerText;
	}
	if (output.dataset.task?.includes('Image')) {
		return copiedOutput.querySelector('img')?.getAttribute('src') || '';
	}
	if (output.dataset.task?.includes('Video')) {
		return copiedOutput.querySelector('video')?.getAttribute('src') || '';
	}
	return output.dataset.outputToSave || copiedOutput.innerText;
};

const markActionComplete = (button: HTMLButtonElement, label: string): void => {
	button.textContent = label;
	button.disabled = true;
};

addToBoardButton.addEventListener('click', () => {
	const graph = globalThis.neuroCreate.graph;
	const task = output.dataset.task as TaskId | SubTaskId | undefined;
	const result = getResultValue();
	if (!graph || graph.graphMode !== 'moodboard' || !task || !result) return;

	const recipe = lastGeneratedRecipe || createCurrentRecipe(task);
	const existingOutputNode = existingOutputNodeForTask(task);
	const wasUpdating = Boolean(existingOutputNode);
	const outputNode = (graph as MoodboardGraph).materialiseSynthesiseOutput(
		result,
		task,
		recipe,
		existingOutputNode
	);
	rememberOutputNodeForTask(task, outputNode);
	recipeStatus.textContent = 'Output is up to date with these inputs';
	recipeStatus.classList.remove('hidden', 'stale');
	recipeStatus.classList.add('up-to-date');
	markActionComplete(
		addToBoardButton,
		wasUpdating ? 'Updated node on board' : 'Added to board'
	);
});

addToNotesButton.addEventListener('click', () => {
	const task = output.dataset.task as TaskId | SubTaskId | undefined;
	const result = getResultValue();
	if (!task || !result) return;
	copyResultToNotePad(result, task);
	tutorialEmitter.emit(TUTORIAL_EVENT_TYPES.addedToNote);
	markActionComplete(addToNotesButton, 'Added to notes');
});

regenerateButton.addEventListener('click', () => {
	regenerateButton.disabled = true;
	handleSubmit();
});

const copyResultToClipboard = async (): Promise<void> => {
	const result = getResultValue();
	if (!result) return;
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(result);
		} else {
			const textarea = document.createElement('textarea');
			textarea.value = result;
			textarea.style.position = 'fixed';
			textarea.style.opacity = '0';
			document.body.appendChild(textarea);
			textarea.select();
			const copied = document.execCommand('copy');
			textarea.remove();
			if (!copied) throw new Error('Copy command failed');
		}
		copyResultButton.textContent = 'Copied';
		setTimeout(() => {
			copyResultButton.textContent = 'Copy';
		}, 1500);
	} catch {
		showAlert('Unable to copy this result. Please select the result text and copy it manually.');
	}
};

copyResultButton.addEventListener('click', () => {
	void copyResultToClipboard();
});

const copyResultToNotePad = (result: string, task: TaskId | SubTaskId): void => {
	if (result !== '' && globalThis.neuroCreate.graph) {
		if (task.includes('Image')) {
			(globalThis.neuroCreate.graph as MoodboardGraph).copyMediaToNote('image', result);
		} else if (task.includes('Video')) {
			(globalThis.neuroCreate.graph as MoodboardGraph).copyMediaToNote('video', result);
		} else {
			(globalThis.neuroCreate.graph as MoodboardGraph).copyToNote(result);
		}

		// Like the result if it's a valid task with a 'likeType' (subtasks not currently supported)
		// const likeType = SYNTHESISE_AI_TASKS[task as TaskId]?.likeType as LikedItem['type'];
		// if (likeType) {
		// 	like(document.body.dataset.boardId!, likeType, result, null, false).then(() => {
		// 		refreshHeartedList();
		// 	});
		// }
	}
};

// Fetch result from Synthesise AI
const getSynthesiseAiResult = async (
	task: TaskId | SubTaskId,
	input: string,
	includeResult = false, // if true add the previous result into the output as well as the new result
	imageUrl?: string
): Promise<string> => {
	try {
		const { result } = await synthesiseAi(document.body.dataset.boardId!, task, input, imageUrl);
		console.log(`Debug output from [${task}]: ${result}`);
		if (includeResult && !task.includes('Image')) {
			return `${input}

${result}`;
		}
		return result;
	} catch (e) {
		removeLoading();
		showAlert(
			'Unable to generate result. Please try again later or contact us if this problem continues.'
		);
		return '';
	}
};

async function fetchResultVariant1(task: TaskId | SubTaskId, likedItems: string) {
	if (task.includes('SCAMPER')) {
		const extractedText = await getSynthesiseAiResult('Extract', likedItems);

		displayOutput(
			likedItems,
			task,
			extractedText,
			await getSynthesiseAiResult(task, extractedText)
		);
	} else if (task.includes('Image')) {
		const unitedText = await getSynthesiseAiResult('Unite', likedItems);
		displayOutput(unitedText, task, unitedText, await getSynthesiseAiResult(task, unitedText));
	} else {
		displayOutput(likedItems, task, await getSynthesiseAiResult(task, likedItems));
	}
}

async function fetchResultVariant2(task: TaskId | SubTaskId, likedItems: string) {
	if (task.includes('SCAMPER')) {
		const extractedText = await getSynthesiseAiResult('Extract', likedItems);

		displayOutput(
			likedItems,
			task,
			extractedText,
			await getSynthesiseAiResult(task, extractedText)
		);
	} else {
		const unitedText = await getSynthesiseAiResult('Unite', likedItems);

		displayOutput(likedItems, task, unitedText, await getSynthesiseAiResult(task, unitedText));
	}
}

async function fetchResultVariant3(task: TaskId | SubTaskId, likedItems: string) {
	displayOutput(likedItems, task, await getSynthesiseAiResult(task, likedItems));
	showNonBlockingMessage('Are you happy with the result?', [
		{
			label: 'Yes',
			onClick: () => {
				clearNonBlockingMessage();
			},
		},
		{
			label: 'No, try an alternative',
			onClick: async () => {
				clearNonBlockingMessage();
				const unitedText = await getSynthesiseAiResult('Unite', likedItems);

				displayOutput(likedItems, task, unitedText, await getSynthesiseAiResult(task, unitedText));
			},
		},
	]);
}
// calling for variant 4-a
async function fetchResultVariant4(task: TaskId | SubTaskId, freeText: string) {
	if (
		task === 'Summary' ||
		task === 'Extract' ||
		task === 'Post' ||
		task.startsWith('Image') ||
		task.startsWith('Video')
	) {
		displayOutput(freeText, task, await getSynthesiseAiResult(task, freeText));
	} else {
		const extractedText = await getSynthesiseAiResult('Extract', freeText);

		displayOutput(freeText, task, extractedText, await getSynthesiseAiResult(task, extractedText));
	}
}

async function fetchResultVariant5A(
	task: TaskId | SubTaskId,
	freeText: string,
	likedItems: string
) {
	const extractedText = await getSynthesiseAiResult('Extract', freeText);
	const mergeInput = ` - ${extractedText || ''}
${likedItems}`;
	const unitedInput = await getSynthesiseAiResult('Unite', mergeInput);
	displayOutput(unitedInput, task, await getSynthesiseAiResult(task, unitedInput));
}

async function fetchResultVariant5B(
	task: TaskId | SubTaskId,
	freeText: string,
	likedItems: string
) {
	const extractFromText = await getSynthesiseAiResult('Extract', freeText);
	const uniteFromNodes = await getSynthesiseAiResult('Unite', likedItems);

	const mergeInput = ` - ${extractFromText || ''}
${uniteFromNodes || ''}`;
	const taskResult = await getSynthesiseAiResult(task, mergeInput);
	displayOutput(mergeInput, task, uniteFromNodes, taskResult);
}

async function fetchResultVariant6Image(task: TaskId | SubTaskId, imageUrl: string) {
	const taskResult = await getSynthesiseAiResult(task, '', false, imageUrl);
	displayOutput('', task, taskResult);
}

const fetchResult = async (
	variant: SynthesiseVariant,
	task: TaskId | SubTaskId,
	freeText: string,
	likedItems: string | undefined,
	imageUrl: string | undefined
): Promise<void> => {
	if (likedItems !== undefined) {
		setLoading();

		if (variant === 'Recipe') {
			const recipe = lastGeneratedRecipe || createCurrentRecipe(task);
			const conceptTitle = conceptualTerritoryTitleFromRecipe(recipe);
			displayOutput(
				conceptTitle,
				task,
				await getSynthesiseAiResult(task, freeText, false, imageUrl)
			);
		} else if (variant === 'Variant 1') {
			await fetchResultVariant1(task, likedItems);
		} else if (variant === 'Variant 2') {
			await fetchResultVariant2(task, likedItems);
		} else if (variant === 'Variant 3') {
			await fetchResultVariant3(task, likedItems);
		} else if (variant === 'Variant 4' || variant === 'Variant 4-A') {
			await fetchResultVariant4(task, freeText);
		} else if (variant === 'Variant 5-A') {
			await fetchResultVariant5A(task, freeText, likedItems);
		} else if (variant === 'Variant 5-B') {
			await fetchResultVariant5B(task, freeText, likedItems);
		} else if (variant === 'Variant 6-Image' && imageUrl) {
			console.log(imageUrl);
			await fetchResultVariant6Image(task, imageUrl);
		}
	}
};

const showDescription = (event: Event, description: HTMLElement): void => {
	const label = event.target as HTMLLabelElement;
	removeChildren(description);
	if (label) {
		const prompt = document.createElement('span');
		description.appendChild(prompt);
		const taskHint = TASK_INPUT_HINTS[label.htmlFor as TaskId];
		prompt.innerText = [label.title, taskHint].filter(Boolean).join(' ');
	}
};

function validateSelectedOptions(
	variant: SynthesiseVariant,
	taskOption: TaskId | SubTaskId,
	subTaskOption: string
): boolean {
	if (variant === 'Invalid Input') {
		showAlert('Invalid Input. Please enter some text or select liked items.');
		return false;
	}

	if (taskOption === null) {
		showAlert('Please select a task', [
			{
				label: 'Close',
				onClick: () => {
					clearNonBlockingMessage();
				},
			},
		]);
		return false;
	}

	const subTaskContainer = form.querySelector('.second-option') as HTMLFieldSetElement;
	if (subTaskContainer.hasChildNodes() && subTaskOption === null) {
		showAlert('Please select a sub task', [
			{
				label: 'Close',
				onClick: () => {
					clearNonBlockingMessage();
				},
			},
		]);
		return false;
	} else if (
		subTaskContainer.hasChildNodes() &&
		form.querySelector('fieldset input:checked+label.soft-disabled')
	) {
		showAlert('This option is not available on your plan. See billing settings to upgrade.', [
			{
				label: 'Open Billing Settings',
				onClick: () => {
					clearNonBlockingMessage();
					window.location.href = '/settings/billing';
				},
			},
			{
				label: 'Close',
				onClick: () => {
					clearNonBlockingMessage();
				},
			},
		]);
		return false;
	}

	return true;
}

const handleSubmit = (event?: Event) => {
	event?.preventDefault();
	syncSelectedNodesIntoRecipe();

	const variant = getVariant();

	const formData = new FormData(form);
	const taskOption = formData.get('first-option') as TaskId;
	const subTaskOption = formData.get('second-option') as SubTaskId;

	if (validateSelectedOptions(variant, taskOption, subTaskOption)) {
		const task = subTaskOption || taskOption;
		const recipe = createCurrentRecipe(task);
		const compiled = compileSynthesisePrompt(recipe);
		const prompt = promptForSynthesiseTask(compiled, recipe.task);
		// Keep a direct visual source all the way through the submission even if
		// a future slot compiler change no longer selects it as the first image.
		const imageUrl =
			compiled.imageUrl ||
			(originalRecipeInput?.role === 'visual-reference' ? originalRecipeInput.src : undefined);
		if (imageUrl) console.info('[synthesise] using image reference', { task, imageUrl });
		lastGeneratedRecipe = recipe;
		void fetchResult(variant, task, prompt, '', imageUrl);
	}
};

export const resetSynthesiseAiPanel = () => {
	synthesiseAiContainer.scrollTop = 0;
	originalInput.value = '';
	additionalInstructions.value = '';
	synthesiseAiButton.classList.add('buttonActive');
	output.innerHTML = '';
	output.classList.add('hidden');
	noResult.classList.remove('hidden');
	resultActions.classList.add('hidden');
	regenerateButton.textContent = 'Regenerate';
	recipeStatus.classList.add('hidden');
	recipeStatus.classList.remove('stale', 'up-to-date');
	ideaInputPicker.classList.add('hidden');
	removeChildren(imageContainer);
	lastGeneratedRecipe = undefined;
	activeRecipeNode = undefined;
	materialisedOutputNodesByTask.clear();
	recipeInputs = [];
	originalRecipeInput = undefined;
	if (globalThis.neuroCreate.graph?.selections.synthesiseNodes !== undefined) {
		globalThis.neuroCreate.graph.stopSelectingNodes('common-ideas');
	}
	boardInputSelection.classList.add('hidden');
	addBoardInputButton.textContent = '+ Board items';
	toneSelect.selectedIndex = 0;
	lengthSelect.selectedIndex = 0;
	formatSelect.selectedIndex = 0;
	renderOriginalInputSource();
	renderRecipeInputs();
};

export const copySelectedTextFromNotepadToPanel = () => {
	if (globalThis.neuroCreate.graph) {
		const selection = globalThis.neuroCreate.graph.getCurrentlySelectedText();

		if (selection && selection.length > 1) {
			return selection;
		}

		if (selection && selection.length <= 1) {
			showAlert('Please select some text in the note to generate Synthesise AI');
		}
		return selection;
	}
};

const setupSynthesiseAiPanel = () => {
	updateLikedNodesInPanel();
	updateTaskOptionsInPanel();
	resultActions.classList.add('hidden');

	tutorialEmitter.on(TUTORIAL_EVENT_TYPES.likedNode, () => {
		updateLikedNodesInPanel();
		updateTaskOptionsInPanel();
	});

	addNumberOfNodeItems(likedInputContainer);
	addNumberOfNodeItems(selectedInputContainer);

	// toggle the liked items and selected items once the panel is opened
	toggleNodeItems(likedInputContainer);
	toggleNodeItems(selectedInputContainer);
};

const prepareSynthesiseAiPanel = (): void => {
	closeAllPanels();
	synthesiseAiContainer.classList.remove('hidden');
	setActiveSynthesiseDockTab('synthesise');
	resetSynthesiseAiPanel();
	aiInput.classList.remove('hidden');
	aiInputImage.classList.add('hidden');
	setupSynthesiseAiPanel();
	requestAnimationFrame(() => {
		if (
			!synthesiseAiContainer.classList.contains('hidden') &&
			!taskSelector.querySelector('label')
		) {
			updateTaskOptionsInPanel();
		}
	});
};

export const openSynthesiseAiPanelWithImage = (url: string) => {
	prepareSynthesiseAiPanel();
	removeChildren(imageContainer);
	const img = document.createElement('img');
	img.src = url;
	img.alt = 'Selected image';
	img.width = 240;
	imageContainer.appendChild(img);
	setOriginalRecipeInput({
		id: createRecipeInputId(),
		role: 'visual-reference',
		label: 'Image reference',
		src: url,
		source: 'notepad',
	});
	updateTaskOptionsInPanel();
};

export const openSynthesiseAiPanel = (input?: string | undefined) => {
	prepareSynthesiseAiPanel();
	if (input) {
		setOriginalRecipeInput({
			id: createRecipeInputId(),
			role: 'brief',
			label: truncateInputLabel(input),
			value: input,
			source: 'notepad',
		});
	}
	updateTaskOptionsInPanel();
};

const getParentTaskId = (task: string): TaskId | undefined => {
	const direct = (Object.keys(SYNTHESISE_AI_TASKS) as TaskId[]).find((taskId) => taskId === task);
	if (direct) return direct;
	return (Object.keys(SYNTHESISE_AI_TASKS) as TaskId[]).find((taskId) =>
		Object.prototype.hasOwnProperty.call(SYNTHESISE_AI_TASKS[taskId].options || {}, task)
	);
};

export const openSynthesiseAiPanelForNode = (node: NCNode): void => {
	prepareSynthesiseAiPanel();
	const content = getSynthesiseNodeInputContent(node);
	// A generated image has a recipe of its own, but selecting it for
	// Synthesise means "use this image as the next visual reference", not
	// "edit the recipe that happened to create it". This also applies to
	// imported/search images. Videos remain output-only references because the
	// media providers currently accept images, not videos, for image-to-video.
	const isVisualSource = Boolean(content.src && !node.isVideo);
	if (!node.synthesiseRecipe || isVisualSource) {
		activeRecipeNode = undefined;
		const input = recipeInputFromNode(node, true);
		// Some persisted visual nodes keep their URL in the structured output
		// rather than node.src. Preserve either representation as the primary
		// visual-reference input so Image and Video compile to image-to-image.
		if (content.src) {
			// Keep the image as an explicit, visible additional-context card. A media
			// node is a reference, not a text brief: the user can add their own
			// transformation instructions in the Original input when needed.
			const visualReference = {
				...input,
				id: createRecipeInputId(),
				role: 'visual-reference' as const,
				label: content.label || 'Selected image',
				value: `Visual reference: ${content.label || 'Selected image'}`,
				src: content.src,
			};
			recipeInputs = [visualReference];
			originalRecipeInput = undefined;
			originalInput.value = '';
			renderOriginalInputSource();
			markRecipeChanged();
			renderRecipeInputs();
			// The dock may still be transitioning from another panel. Render once
			// more on the next frame so the visible Additional context list cannot
			// lag behind the recipe state until the user changes tabs.
			requestAnimationFrame(renderRecipeInputs);
		} else {
			setOriginalRecipeInput(input);
		}
		if (content.src) {
			const img = document.createElement('img');
			img.src = content.src;
			img.alt = 'Selected image';
			img.width = 240;
			imageContainer.appendChild(img);
		}
		updateTaskOptionsInPanel();
		return;
	}

	const recipe = node.synthesiseRecipe;
	rememberOutputNodeForTask(recipe.task, node);
	const [savedOriginalInput, ...savedAdditionalInputs] = recipe.inputs;
	const currentOriginalNode = savedOriginalInput?.nodeUid
		? globalThis.neuroCreate.graph?.nodes.find(
				(candidate) => candidate.uid === savedOriginalInput.nodeUid
			)
		: undefined;
	if (savedOriginalInput) {
		setOriginalRecipeInput({
			...savedOriginalInput,
			value: currentOriginalNode?.label || savedOriginalInput.value,
			src: currentOriginalNode?.src || savedOriginalInput.src,
		});
	}
	recipeInputs = savedAdditionalInputs.map((input) => ({ ...input }));
	recipeInputs = getCurrentRecipeInputs().slice(savedOriginalInput ? 1 : 0);
	additionalInstructions.value = recipe.instructions || '';
	if (recipe.settings?.tone) toneSelect.value = recipe.settings.tone;
	if (recipe.settings?.platform) {
		// Reddit is retained in the saved-recipe contract for backwards
		// compatibility, but is no longer offered for new Social Post outputs.
		const savedPlatformStillAvailable = Array.from(platformSelect.options).some(
			(option) => option.value === recipe.settings?.platform
		);
		platformSelect.value = savedPlatformStillAvailable ? recipe.settings.platform : 'LinkedIn';
	}
	platformSetting.classList.toggle('hidden', recipe.task !== 'Post');
	if (recipe.settings?.length) lengthSelect.value = recipe.settings.length;
	if (recipe.settings?.format) formatSelect.value = recipe.settings.format;
	renderRecipeInputs();
	updateTaskOptionsInPanel();

	const parentTask = getParentTaskId(recipe.task);
	if (parentTask) {
		clickLabels(parentTask);
		if (parentTask !== recipe.task) clickLabels(recipe.task);
	}

	lastGeneratedRecipe = recipe;
	displayOutput(
		'',
		recipe.task as TaskId | SubTaskId,
		node.src || node.label || '',
		undefined,
		true
	);
	addToBoardButton.textContent = UPDATE_NODE_ON_BOARD_LABEL;
	const currentRecipe = createCurrentRecipe(recipe.task as TaskId | SubTaskId);
	const sourceNodeIds = new Set(
		globalThis.neuroCreate.graph?.nodes.map((source) => source.uid) || []
	);
	const hasMissingSource = recipe.inputs.some(
		(input) => input.nodeUid && !sourceNodeIds.has(input.nodeUid)
	);
	const isStale = hasMissingSource || currentRecipe.inputSnapshot !== recipe.inputSnapshot;
	recipeStatus.textContent = isStale
		? 'Inputs changed · regenerate to update this output'
		: 'Output is up to date with these inputs';
	recipeStatus.classList.remove('hidden');
	recipeStatus.classList.toggle('stale', isStale);
	recipeStatus.classList.toggle('up-to-date', !isStale);
	addToBoardButton.disabled = isStale;
};

if (synthesiseAiButton) {
	synthesiseAiButton.addEventListener('click', () => {
		if (!synthesiseAiContainer.classList.contains('hidden')) {
			synthesiseAiButton.classList.remove('buttonActive');
			synthesiseAiContainer.classList.add('hidden');
			resetSynthesiseAiPanel();
		} else {
			openSynthesiseAiPanel();
			syncActiveBoardNodeToOriginalInput();
		}

		if (window.innerWidth < WINDOW_BREAKPOINTS.laptop) {
			closeNotepad();
		}
	});
	window.addEventListener(SYNTHESISE_DOCK_TAB_ACTIVATED_EVENT, (event) => {
		const detail = (event as CustomEvent<{ tab?: string }>).detail;
		if (detail?.tab === 'synthesise' && !synthesiseAiContainer.classList.contains('hidden')) {
			// A recipe opened from a node (especially an image reference) is
			// already intentional context. Do not replace it just because the user
			// briefly visits Idea Box or Notepad and comes back to this dock tab.
			if (!originalRecipeInput && recipeInputs.length === 0) {
				syncActiveBoardNodeToOriginalInput();
			} else {
				renderOriginalInputSource();
				renderRecipeInputs();
			}
		}
	});
	selectItemsButton.addEventListener('click', handleSelectItems);
	form.addEventListener('submit', handleSubmit);
	originalInput.addEventListener('input', () => {
		ensureManualOriginalInput();
		if (originalRecipeInput) {
			originalRecipeInput.value = originalInput.value.trim() || undefined;
			if (!originalRecipeInput.nodeUid) {
				originalRecipeInput.label = truncateInputLabel(
					originalInput.value.trim() || 'Original input'
				);
			}
		}
		markRecipeChanged();
		updateTaskOptionsInPanel();
	});
	additionalInstructions.addEventListener('input', markRecipeChanged);
	for (const select of [toneSelect, lengthSelect, formatSelect]) {
		select.addEventListener('change', markRecipeChanged);
	}
	visualStyleSelect.addEventListener('change', () => {
		if (!visualStyleSelect.value) return;
		clickLabels(visualStyleSelect.value);
		markRecipeChanged();
		const { selectedOption } = getCurrentlySelectedTaskOptions();
		if (selectedOption === 'Image' || selectedOption === 'Video') {
			handleGenerateImageAndVideoButton(selectedOption, visualStyleSelect.value);
		}
	});
	addBoardInputButton.addEventListener('click', handleSelectItems);
	finishBoardInputSelectionButton.addEventListener('click', handleSelectItems);
	addIdeaInputButton.addEventListener('click', () => void showIdeaInputPicker());
	addNoteInputButton.addEventListener('click', openNotepad);
	closeIdeaInputPicker.addEventListener('click', () => ideaInputPicker.classList.add('hidden'));
	likedItemCheckbox.addEventListener('change', () => {
		updateLikedNodesInPanel();
		updateTaskOptionsInPanel();
		toggleNodeItems(likedInputContainer);
	});
	selectedItemCheckbox.addEventListener('change', () => {
		updateSelectedNodesInPanel();
		updateTaskOptionsInPanel();
		toggleNodeItems(selectedInputContainer);
	});
}

globalThis.updateTaskOptionsInPanel = updateTaskOptionsInPanel;
